import { useRef, useEffect } from "react";
import { normalizeEmotion } from "../../utils/emotion";
import { CONTENT_H, CONTENT_W, clamp, hash01, lerp, mixHexColors, normalizeHexColor, randInt } from "./chunk-0.jsx";
import { styleForIrisEyes } from "./chunk-18.jsx";
import { drawIrisEye } from "./chunk-20.jsx";
export function UncannyEyes({
  emotion,
  variant,
  poke,
  heartbeatRef,
  accent,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const emotionRef = useRef(emotion);
  const variantRef = useRef(variant);
  const accentRef = useRef(accent);
  const pokeRef = useRef(poke);
  useEffect(() => {
    emotionRef.current = emotion;
  }, [emotion]);
  useEffect(() => {
    variantRef.current = variant;
  }, [variant]);
  useEffect(() => {
    accentRef.current = accent;
  }, [accent]);
  useEffect(() => {
    pokeRef.current = poke;
  }, [poke]);
  const stateRef = useRef({
    gazeX: 0,
    gazeY: 0,
    tgtX: 0,
    tgtY: 0,
    nextGazeAt: 0,
    blink: 0,
    blinkDir: 0,
    nextBlinkAt: 0,
    lastPoke: 0,
    lastT: 0
  });
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    const s = stateRef.current;
    s.nextBlinkAt = Date.now() + 600;
    s.nextGazeAt = Date.now() + 300;
    let raf = 0;
    const loop = t => {
      heartbeatRef.current = Date.now();
      const now = Date.now();
      const dt = s.lastT ? now - s.lastT : 16;
      s.lastT = now;
      const v = String(variantRef.current || '').toLowerCase();
      const seed = hash01(`uncanny:${v}`);
      const gazeLerp = motionBoost ? 0.065 : 0.04;
      const gazeAmpX = motionBoost ? 1.05 : 0.9;
      const gazeAmpY = motionBoost ? 0.75 : 0.6;
      if (!s.nextGazeAt || now >= s.nextGazeAt) {
        s.tgtX = (Math.random() * 2 - 1) * gazeAmpX;
        s.tgtY = (Math.random() * 2 - 1) * gazeAmpY;
        s.nextGazeAt = now + (motionBoost ? 700 : 1200) + randInt(motionBoost ? 900 : 1800);
      }
      s.gazeX = lerp(s.gazeX, s.tgtX, gazeLerp);
      s.gazeY = lerp(s.gazeY, s.tgtY, gazeLerp);
      if (motionBoost) {
        const driftX = Math.sin(t / 900 + seed * 12) * 0.12;
        const driftY = Math.sin(t / 760 + seed * 7 + 1.2) * 0.08;
        s.gazeX = clamp(s.gazeX + driftX, -1, 1);
        s.gazeY = clamp(s.gazeY + driftY, -1, 1);
      }
      const pokeNow = pokeRef.current || 0;
      if (pokeNow !== s.lastPoke) {
        s.lastPoke = pokeNow;
        s.blinkDir = 1;
        s.nextBlinkAt = now + (motionBoost ? 1400 : 2200) + randInt(motionBoost ? 1600 : 2800);
      }
      if (s.blinkDir === 0 && now >= s.nextBlinkAt) {
        s.blinkDir = 1;
      }
      if (s.blinkDir !== 0) {
        s.blink = clamp(s.blink + s.blinkDir * dt / (motionBoost ? 70 : 90), 0, 1);
        if (s.blinkDir === 1 && s.blink >= 1) s.blinkDir = -1;
        if (s.blinkDir === -1 && s.blink <= 0) {
          s.blinkDir = 0;
          s.nextBlinkAt = now + (motionBoost ? 1600 : 2400) + randInt(motionBoost ? 2200 : 3600);
        }
      }
      const emo = normalizeEmotion(emotionRef.current);
      const style = styleForIrisEyes(emo);
      const opennessBase = clamp((style.openness ?? 0.85) - s.blink * 1.15, 0, 1.15);
      const pupilWave = motionBoost ? 0.085 : 0.05;
      const pupilScaleBase = clamp((style.pupil ?? 0.85) + Math.sin(t / (motionBoost ? 900 : 1100)) * pupilWave, 0.35, 1.35);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const irisHue = (() => {
        const styleHex = normalizeHexColor(style.iris);
        if (styleHex) return styleHex;
        const base = (() => {
          if (v === 'halloween_skull') return '#fb7185';
          if (v === 'ttgo_tdisplay') return '#60a5fa';
          if (v === 'dual_lcd') return '#34d399';
          if (v === 'teensy') return '#a78bfa';
          if (v === 'esp8266') return '#f59e0b';
          if (v === 'esp32_redo') return '#f97316';
          if (v === 'esp32') return '#22c55e';
          if (v === 'adafruit') return '#10b981';
          return '#22c55e';
        })();
        const accentHex = normalizeHexColor(accentRef.current);
        return accentHex ? mixHexColors(base, accentHex, 0.25) : base;
      })();
      const eyeR = (() => {
        if (v === 'dual_lcd') return 50;
        if (v === 'ttgo_tdisplay') return 48;
        if (v === 'esp8266') return 46;
        if (v === 'esp32_redo') return 54;
        if (v === 'esp32') return 52;
        if (v === 'teensy') return 56;
        if (v === 'adafruit') return 58;
        if (v === 'halloween_skull') return 60;
        return 52;
      })();
      const eyeSpread = (() => {
        if (v === 'dual_lcd') return 0.28;
        if (v === 'ttgo_tdisplay') return 0.36;
        if (v === 'esp8266') return 0.35;
        return 0.32;
      })();
      const cxL = canvas.width * (0.5 - eyeSpread);
      const cxR = canvas.width * (0.5 + eyeSpread);
      const cy = canvas.height * (v === 'ttgo_tdisplay' ? 0.54 : 0.52);
      const winkLeft = Boolean(style.wink) ? Number(pokeRef.current || 0) % 2 === 0 : false;
      const winkRight = Boolean(style.wink) ? !winkLeft : false;
      const opennessL = winkLeft ? Math.min(opennessBase, 0.12) : opennessBase;
      const opennessR = winkRight ? Math.min(opennessBase, 0.12) : opennessBase;
      const baseBrow = typeof style.brow === 'number' ? style.brow : 0;
      const browL = style.asym ? baseBrow + 0.14 : baseBrow;
      const browR = style.asym ? baseBrow - 0.14 : baseBrow;
      let gazeXL = s.gazeX;
      let gazeXR = s.gazeX;
      let gazeY = s.gazeY;
      if (style.cross) {
        gazeXL = 0.85;
        gazeXR = -0.85;
        gazeY = 0.1;
      }
      drawIrisEye(ctx, cxL, cy, eyeR, {
        openness: opennessL,
        pupilScale: pupilScaleBase,
        gazeX: gazeXL,
        gazeY,
        iris: irisHue,
        brow: browL,
        side: 'left'
      });
      drawIrisEye(ctx, cxR, cy, eyeR, {
        openness: opennessR,
        pupilScale: pupilScaleBase,
        gazeX: gazeXR,
        gazeY,
        iris: irisHue,
        brow: browR,
        side: 'right'
      });
      if (style.blush) {
        ctx.save();
        ctx.fillStyle = 'rgba(251,113,133,0.28)';
        ctx.beginPath();
        ctx.ellipse(cxL, cy + eyeR * 0.82, eyeR * 0.36, eyeR * 0.18, 0, 0, Math.PI * 2);
        ctx.ellipse(cxR, cy + eyeR * 0.82, eyeR * 0.36, eyeR * 0.18, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      if (style.tear) {
        ctx.save();
        ctx.fillStyle = 'rgba(56,189,248,0.9)';
        ctx.beginPath();
        ctx.arc(cxR + eyeR * 0.35, cy + eyeR * 0.65, eyeR * 0.12, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      if (style.drool) {
        ctx.save();
        ctx.fillStyle = 'rgba(56,189,248,0.75)';
        ctx.beginPath();
        ctx.ellipse(canvas.width * 0.52, canvas.height * 0.78, 10, 18, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      if (style.shades) {
        ctx.save();
        ctx.globalAlpha = 0.72;
        ctx.fillStyle = '#0b1220';
        const h = eyeR * 0.62;
        const y = cy - eyeR * 0.25;
        ctx.fillRect(cxL - eyeR * 1.05, y, eyeR * 2.1, h);
        ctx.fillRect(cxR - eyeR * 1.05, y, eyeR * 2.1, h);
        ctx.globalAlpha = 0.9;
        ctx.fillRect(canvas.width * 0.48, cy - 6, canvas.width * 0.04, 12);
        ctx.restore();
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, heartbeatRef, motionBoost]);
  return <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} width={CONTENT_W} height={CONTENT_H} style={{
      width: '100%',
      height: '100%'
    }} />
    </div>;
}
